import { Box, Flex, Icon, IconButton, Text, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons";
import { FaChevronLeft, FaChevronRight, FaFileContract, FaInfo, FaShieldAlt, FaShoppingCart } from "react-icons/fa";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useSub } from "../../../common/subscription/useSub";
import { useOrganizationProfileUrls } from "../useOrganizationProfileUrls";

interface SidebarItemProps {
  icon: IconType;
  label: string;
  to: string;
  isActive: boolean;
  isCollapsed?: boolean;
}

const SidebarItem = ({ label, icon, to, isActive, isCollapsed }: SidebarItemProps) => {
  return (
    <Link to={to} style={{ width: "100%" }}>
      <Flex
        align="center"
        borderRadius="lg"
        role="group"
        width="full"
        cursor="pointer"
        _hover={{
          bg: "smBackgroundSecondary",
        }}
        bg={isActive ? "smPrimarySofter" : "transparent"}
        color={isActive ? "smPrimary" : undefined}
        p="4"
      >
        <Icon mr={isCollapsed ? "0" : "4"} fontSize="16" as={icon} color={isActive ? "smPrimary" : undefined} />
        {!isCollapsed && <Text fontWeight={isActive ? "medium" : "normal"}>{label}</Text>}
      </Flex>
    </Link>
  );
};

export const OrgProfileSidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;
  const [searchParams] = useSearchParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { hasOrganizationAssessmentAccess } = useSub();
  const profileUrls = useOrganizationProfileUrls();

  const currentSection = currentPath.split("/").pop() || "information";

  const menuItems = [
    {
      label: t("Details"),
      icon: FaInfo,
      to: profileUrls.section("information", searchParams),
    },
    {
      label: t("Contracts"),
      icon: FaFileContract,
      to: profileUrls.section("contracts", searchParams),
    },
    {
      label: t("Sourcing Events"),
      icon: FaShoppingCart,
      to: profileUrls.section("sourcing-events", searchParams),
    },
    ...(hasOrganizationAssessmentAccess
      ? [
          {
            label: t("Reviews & Risk"),
            icon: FaShieldAlt,
            to: profileUrls.section("reviews-risk", searchParams),
          },
        ]
      : []),
  ];

  return (
    <Box
      as="nav"
      minW={isCollapsed ? "60px" : "240px"}
      height="full"
      borderRight="1px solid"
      borderColor="smBorder"
      position="relative"
      transition="all 0.2s"
      px={4}
      py={12}
    >
      <IconButton
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        icon={isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
        size="sm"
        position="absolute"
        right="-4"
        top="2.5"
        zIndex="2"
        borderRadius="full"
        variant="outline"
        backgroundColor="smBackground"
        onClick={() => setIsCollapsed(!isCollapsed)}
      />
      <VStack spacing="2" align="stretch" width="full">
        {menuItems.map((item) => (
          <SidebarItem
            key={item.label}
            {...item}
            isActive={currentSection === item.to.split("/").pop()?.split("?")[0]}
            isCollapsed={isCollapsed}
          />
        ))}
      </VStack>
    </Box>
  );
};
